import type { ReactNode } from "react";
import type { NewConfirmationFormData, NewConfirmationSelectOption } from "../types/new-confirmation.types";
import { CURRENCY_OPTIONS } from "../constants/currencies.config";
import { getBankOptionsForCurrency } from "../constants/paymentMethodsByCurrency.config";
import { FileUpload } from "@/shared/components/form/FileUpload";
import { FieldError } from "@/shared/components/form/FieldError";
import { cn } from "@/lib/utils";
import useTranslations from "@/shared/lang/useTranslations";

const inputBase =
  "w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 dark:text-white transition-all h-12 px-4";
const labelBase = "block text-sm font-medium text-gray-600 dark:text-gray-400";

type NewConfirmationFormProps = {
  value: NewConfirmationFormData;
  onChange: (data: NewConfirmationFormData) => void;
  errors?: Partial<Record<keyof NewConfirmationFormData, string>>;
  disabled?: boolean;
  hideAmountAndBank?: boolean;
  hideComprobante?: boolean;
  showClientAndPayerFields?: boolean;
  /** Si se define, sustituye el bloque cliente/pagador (p. ej. selectores de clientes verificados en pagos). */
  clientPayerSection?: ReactNode;
  /** Deshabilita tipo de moneda y banco (p. ej. fijados por la cuenta en pagos). */
  disableCurrencyBankDateFields?: boolean;
  /** Deshabilita monto, fecha y nota (p. ej. hasta elegir cuenta del beneficiario en pagos). */
  disableAmountNoteFields?: boolean;
};

export const NewConfirmationForm = ({
  value,
  onChange,
  errors = {},
  disabled = false,
  hideAmountAndBank = false,
  hideComprobante = false,
  showClientAndPayerFields = true,
  clientPayerSection,
  disableCurrencyBankDateFields = false,
  disableAmountNoteFields = false,
}: NewConfirmationFormProps) => {
  const { t } = useTranslations("confirmations");
  const currencyBankOnlyDisabled = disabled || disableCurrencyBankDateFields;
  const amountNoteDateDisabled = disabled || disableAmountNoteFields;
  const update = (field: keyof NewConfirmationFormData) => (
    val: string | boolean | NewConfirmationSelectOption | File | null
  ) => {
    if (field === "comprobante" && (val === null || val instanceof File)) {
      onChange({ ...value, comprobante: val instanceof File ? val : null });
      return;
    }
    if (typeof val === "boolean") {
      const next = { ...value, [field]: val };
      if (field === "pagadorEsMismoCliente" && val === true) {
        next.nombrePagador = value.cliente;
      }
      onChange(next);
      return;
    }
    if (typeof val === "object" && val !== null && "value" in val) {
      onChange({ ...value, [field]: (val as NewConfirmationSelectOption).value });
      return;
    }
    const next = { ...value, [field]: (val as string) ?? "" };
    if (field === "tipoMoneda") {
      const options = getBankOptionsForCurrency(next.tipoMoneda);
      const bancoValido = options.some((o) => o.value === value.banco);
      if (!bancoValido) next.banco = "";
    }
    if (field === "cliente" && value.pagadorEsMismoCliente) {
      next.nombrePagador = next.cliente;
    }
    onChange(next);
  };

  return (
    <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
      {showClientAndPayerFields && (
        <>
          {clientPayerSection ?? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
                <div className="space-y-2">
                  <label htmlFor="cliente" className={labelBase}>
                    {t("form_label_client")}
                  </label>
                  <input
                    id="cliente"
                    type="text"
                    value={value.cliente}
                    onChange={(e) => update("cliente")(e.target.value)}
                    placeholder={t("form_placeholder_client")}
                    disabled={disabled}
                    className={inputBase}
                  />
                  <FieldError error={errors.cliente} />
                </div>
                <div className="flex items-center space-x-3 mb-2">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={value.pagadorEsMismoCliente}
                      onChange={(e) => update("pagadorEsMismoCliente")(e.target.checked)}
                      disabled={disabled}
                      className="sr-only peer"
                    />
                    <div className="relative w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-500 dark:peer-focus:ring-green-800 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-gray-300 after:rounded-full after:h-5 after:w-5 after:transition-all dark:after:border-gray-600 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:bg-green-500" />
                    <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                      {t("form_checkbox_same_client")}
                    </span>
                  </label>
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="pagador" className={labelBase}>
                  {t("form_label_beneficiary")}
                </label>
                <input
                  id="pagador"
                  type="text"
                  value={value.nombrePagador}
                  onChange={(e) => update("nombrePagador")(e.target.value)}
                  placeholder={t("form_placeholder_beneficiary")}
                  disabled={disabled || value.pagadorEsMismoCliente}
                  className={inputBase}
                />
                <FieldError error={errors.nombrePagador} />
              </div>
            </div>
          )}
          <hr className="border-gray-100 dark:border-gray-800" />
        </>
      )}
      <div className={cn("grid gap-6", hideAmountAndBank ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4")}>
        {!hideAmountAndBank && (
          <div className="space-y-2 min-w-0 lg:max-w-[22rem]">
            <label htmlFor="monto" className={labelBase}>
              {t("form_label_amount")}
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                $
              </span>
              <input
                id="monto"
                type="text"
                inputMode="decimal"
                value={value.monto}
                onChange={(e) => {
                  let v = e.target.value.replace(/[^\d,.]/g, "");
                  const parts = v.split(/[,.]/);
                  if (parts.length > 2) {
                    const sep = v.includes(".") ? "." : ",";
                    v = parts[0] + sep + parts.slice(1).join("");
                  }
                  update("monto")(v);
                }}
                disabled={amountNoteDateDisabled}
                className={cn(inputBase, "pl-8")}
              />
            </div>
            <FieldError error={errors.monto} />
          </div>
        )}
        <div className="space-y-2 min-w-0">
          <label htmlFor="moneda" className={labelBase}>
            {t("form_label_currency")}
          </label>
          <select
            id="moneda"
            value={value.tipoMoneda}
            onChange={(e) => update("tipoMoneda")(e.target.value)}
            disabled={currencyBankOnlyDisabled}
            className={cn(inputBase, "w-full min-w-0")}
          >
            <option value="">{t("form_placeholder_currency")}</option>
            {CURRENCY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <FieldError error={errors.tipoMoneda} />
        </div>
        {!hideAmountAndBank && (
          <>
            <div className="space-y-2 min-w-0 sm:col-span-2 lg:col-span-1">
              <label htmlFor="banco" className={labelBase}>
                {t("form_label_bank")}
              </label>
              <select
                id="banco"
                value={value.banco}
                onChange={(e) => update("banco")(e.target.value)}
                disabled={currencyBankOnlyDisabled}
                className={cn(inputBase, "font-medium w-full min-w-0")}
              >
                <option value="">
                  {value.tipoMoneda
                    ? t("form_placeholder_bank")
                    : t("form_placeholder_bank_currency_first")}
                </option>
                {getBankOptionsForCurrency(value.tipoMoneda).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <FieldError error={errors.banco} />
            </div>
            <div className="space-y-2 min-w-0 w-full sm:max-w-[10rem]">
              <label htmlFor="fecha" className={labelBase}>
                {t("form_label_date")}
              </label>
              <input
                id="fecha"
                type="date"
                value={value.fecha}
                onChange={(e) => update("fecha")(e.target.value)}
                disabled={amountNoteDateDisabled}
                className={inputBase}
              />
              <FieldError error={errors.fecha} />
            </div>
          </>
        )}
        {hideAmountAndBank && (
          <div className="space-y-2">
            <label htmlFor="fecha" className={labelBase}>
              {t("form_label_date")}
            </label>
            <input
              id="fecha"
              type="date"
              value={value.fecha}
              onChange={(e) => update("fecha")(e.target.value)}
              disabled={amountNoteDateDisabled}
              className={inputBase}
            />
            <FieldError error={errors.fecha} />
          </div>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="nota" className={labelBase}>
          {t("form_label_note")}
        </label>
        <textarea
          id="nota"
          value={value.nota}
          onChange={(e) => update("nota")(e.target.value)}
          placeholder={t("form_placeholder_note")}
          disabled={amountNoteDateDisabled}
          rows={2}
          className={cn(
            inputBase,
            "h-auto py-3 min-h-[80px] resize-none"
          )}
        />
        <FieldError error={errors.nota} />
      </div>

      {!hideComprobante && (
        <FileUpload
          value={value.comprobante}
          onChange={(file) => update("comprobante")(file)}
          label={t("form_label_comprobante")}
          description={t("form_comprobante_description")}
          instructionText={t("form_comprobante_instruction")}
          instructionTextDrop={t("form_comprobante_instruction_drop")}
          sizeLine={t("form_comprobante_size_format", { max: 10 })}
          previewAlt={t("form_comprobante_preview_alt")}
          changeLabel={t("form_comprobante_change")}
          removeLabel={t("form_comprobante_remove")}
          maxSizeMB={10}
          disabled={disabled}
          error={errors.comprobante}
          inputId="comprobante-confirmacion"
        />
      )}
    </form>
  );
};
